import React from "react"
import { useSelector } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    CcRequirementVersion,
    CcRequirementReviewMethod,
    CcRequirementSmartCompliance,
} from "server/models"
import { StoreType } from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    PageForm,
    FormGroup,
    Label,
    Input,
    Textarea,
    DivInput,
    UserIcon,
    CheckboxWrapper,
    CheckButton,
} from "client/components"

interface RequirementVersionProps {
    version: CcRequirementVersion
}

const VersionView = ({
    version,
}: RequirementVersionProps) => {
    const userList = useSelector((state: StoreType) => state.user.userList)
    const lang = useSelector((state: StoreType) => state.system.lang)

    const { reviewMethodsParsed, smartComplianceParsed } = version

    const columnNumber = 3

    const methodColumnLength = Math.ceil(reviewMethodsParsed.length / columnNumber)
    const methodColumns: { [columnIndex: number]: CcRequirementReviewMethod[] } = {}
    for (let i = 0; i < columnNumber; i++) {
        const column = []
        const start = i * methodColumnLength
        const end = start + methodColumnLength
        for (let k = start; k < end; k++) {
            const method = reviewMethodsParsed[k]
            column.push(method)
        }
        methodColumns[i] = column
    }

    const complianceColumnLength = Math.ceil(smartComplianceParsed.length / columnNumber)
    const complianceColumns: { [columnIndex: number]: CcRequirementSmartCompliance[] } = {}
    for (let i = 0; i < columnNumber; i++) {
        const column = []
        const start = i * complianceColumnLength
        const end = start + complianceColumnLength
        for (let k = start; k < end; k++) {
            const compliance = smartComplianceParsed[k]
            column.push(compliance)
        }
        complianceColumns[i] = column
    }

    return (
        <Container>
            <PageHeader>
                <PageTitle>{`${lang.version} # ${version.id || ""}`}</PageTitle>
            </PageHeader>
            <PageContent>
                <PageForm>
                    <Row>
                        <Col xs={12} md={4}>
                            <FormGroup>
                                <Label>{lang.uniqueId}</Label>
                                <Input type="text" value={version.uniqueId} readOnly />
                            </FormGroup>
                        </Col>
                        <Col xs={12} md={8}>
                            <FormGroup>
                                <Label>{lang.name}</Label>
                                <Input type="text" value={version.name} readOnly />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12}>
                            <FormGroup>
                                <Label>{lang.description}</Label>
                                <Textarea value={version.description} readOnly />
                            </FormGroup>

                            <FormGroup>
                                <Label>{lang.type}</Label>
                                <Input type="text" value={version.typeParsed ? version.typeParsed.name : ""} readOnly />
                            </FormGroup>

                            <FormGroup>
                                <Label>{lang.priority}</Label>
                                <Input type="text" value={version.priorityParsed ? version.priorityParsed.name : ""} readOnly />
                            </FormGroup>

                            <FormGroup>
                                <Label>{lang.status}</Label>
                                <Input type="text" value={version.statusParsed ? version.statusParsed.name : ""} readOnly />
                            </FormGroup>

                            <FormGroup>
                                <Label>{lang.createdBy}</Label>
                                <DivInput><UserIcon user={userList[version.creator]} size="sm" /></DivInput>
                            </FormGroup>

                            <FormGroup>
                                <Label>{lang.reviewMethods}</Label>
                                <CheckboxWrapper>
                                    {
                                        Object.keys(methodColumns).map((index) => (
                                            <Col key={index} xs={12} md={12 / columnNumber}>
                                                {
                                                    methodColumns[Number(index)].map((method) => method && (
                                                        <CheckButton
                                                            key={method.id}
                                                            checked={method.enabled}
                                                            title={method.name}
                                                        />
                                                    ))
                                                }
                                            </Col>
                                        ))
                                    }
                                </CheckboxWrapper>
                            </FormGroup>

                            <FormGroup>
                                <Label>{lang.smartCompliance}</Label>
                                <CheckboxWrapper>
                                    {
                                        Object.keys(complianceColumns).map((index) => (
                                            <Col key={index} xs={12} md={12 / columnNumber}>
                                                {
                                                    complianceColumns[Number(index)].map((compliance) => compliance && (
                                                        <CheckButton
                                                            key={compliance.id}
                                                            checked={compliance.enabled}
                                                            title={compliance.name}
                                                        />
                                                    ))
                                                }
                                            </Col>
                                        ))
                                    }
                                </CheckboxWrapper>
                            </FormGroup>
                        </Col>
                    </Row>
                </PageForm>
            </PageContent>
        </Container>
    )
}
export default VersionView
